import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../../styles.css";

const TrapAnnotateSection = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: "🔍",
      title: "Smart Annotation",
      description: "AI-powered tools for precise mosquito specimen annotation",
    },
    {
      icon: "🤖",
      title: "Automated Analysis",
      description: "Machine learning algorithms that accelerate data processing",
    },
    {
      icon: "📊",
      title: "Real-time Insights",
      description: "Instant visualization and reporting of annotated data",
    },
    {
      icon: "👥",
      title: "Collaborative Workspace",
      description: "Team-based annotation with role management and tracking",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="relative py-20 px-4 overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl animate-pulse" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-semibold rounded-full">
              NEW RELEASE
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Introducing{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Digitomy Trap Annotate
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            The next-generation platform for mosquito specimen annotation and
            analysis. Designed for researchers, by researchers.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/auth")}
            className="group relative px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-lg font-semibold rounded-full overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/50"
          >
            <span className="relative z-10 flex items-center space-x-2">
              <span>Get Started</span>
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </motion.button>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.05, y: -5 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur opacity-0 group-hover:opacity-75 transition-opacity duration-300" />
              <div className="relative p-6 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 h-full">
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl blur-xl" />
          <div className="relative bg-white/5 backdrop-blur-lg border border-white/20 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Ready to revolutionize your workflow?
                </h3>
                <p className="text-gray-300 text-lg mb-6">
                  Join leading research institutions already using Digitomy Trap
                  Annotate to streamline their mosquito surveillance operations.
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Increase annotation speed by 10x</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Improve accuracy with AI assistance</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Seamless team collaboration</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-col items-center space-y-4">
                <div className="w-full max-w-sm bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/30">
                  <div className="text-center mb-6">
                    <h4 className="text-2xl font-bold text-white mb-2">
                      Access the Platform
                    </h4>
                    <p className="text-gray-300">
                      Sign in with your organization credentials
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/auth")}
                    className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-2xl"
                  >
                    Launch Trap Annotate →
                  </motion.button>
                </div>
                <p className="text-gray-400 text-sm text-center">
                  Don't have an account?{" "}
                  <a
                    href="/#contact"
                    className="text-indigo-400 hover:text-indigo-300 underline"
                  >
                    Contact us
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tech Stack Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-400 text-sm mb-4">Powered by</p>
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400">
            <span className="text-lg font-semibold">React</span>
            <span className="text-2xl">•</span>
            <span className="text-lg font-semibold">TensorFlow</span>
            <span className="text-2xl">•</span>
            <span className="text-lg font-semibold">Label Studio</span>
            <span className="text-2xl">•</span>
            <span className="text-lg font-semibold">Cloud Infrastructure</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TrapAnnotateSection;

